import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useHubModuleWriteAny } from "@/lib/useHubModuleWrite";
import { ZPL_PRINTER_STORAGE_KEYS } from "@/lib/zplPrinter";
import { useZplPrintLocation } from "@/lib/zplPrintLocation";
import { EM_DASH } from "@/lib/utils";
import { qcIncidentPath } from "@/lib/qc/qcIncidentNav";

import {
  fetchAnalysisTransfer,
  printAnalysisTransferLabel,
  returnAnalysisTransfer,
} from "./analysisTransfersApi";

export function QualityAnalysisLoanDetailPage() {
  const params = useParams();
  const transferId = Number(params.transferId);
  const writesAllowed = useHubModuleWriteAny(["quality", "manufacturing"]);
  const { showToast } = useHubToast();
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useZplPrintLocation({
    storageKey: ZPL_PRINTER_STORAGE_KEYS.qualityAnalysisLoans,
    documents: [{ id: "loan", label: "Loan", labelSize: "4x2", dpi: 300 }],
  });
  const detailQuery = useQuery({
    queryKey: ["quality", "analysis-transfers", "detail", transferId],
    queryFn: () => fetchAnalysisTransfer(transferId),
    enabled: Number.isFinite(transferId) && transferId > 0,
  });
  const reprintMutation = useMutation({
    mutationFn: () => printAnalysisTransferLabel(transferId, selectedPrinter.trim()),
    onSuccess: () => showToast({ tone: "success", message: "Loan label sent to the printer." }),
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not print the loan label.",
      });
    },
  });
  const row = detailQuery.data?.row;
  const printerReady = selectedPrinter.trim() !== "";

  if (!Number.isFinite(transferId) || transferId < 1) {
    return <p className="text-sm text-slate-600">Invalid analysis loan id.</p>;
  }

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Open from the loan label Data Matrix or the analysis loans list.">
        Analysis loan {transferId}
      </PageTitleWithHelp>
      {detailQuery.isLoading ? (
        <p className="text-sm text-slate-600">Loading…</p>
      ) : detailQuery.isError || row == null ? (
        <p className="text-sm text-slate-600">This analysis loan was not found.</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="font-mono">{row.deviceLabelSerial}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p>
              <span className="font-medium text-slate-700">Destination:</span> {row.destinationName ?? EM_DASH}
            </p>
            <p>
              <span className="font-medium text-slate-700">Sent:</span> {row.sentAt} by {row.sentBy}
            </p>
            {row.returnedAt != null ? (
              <p>
                <span className="font-medium text-slate-700">Returned:</span> {row.returnedAt} by{" "}
                {row.returnedBy ?? EM_DASH}
              </p>
            ) : (
              <p className="text-emerald-800">Open loan</p>
            )}
            {row.notes ? (
              <p>
                <span className="font-medium text-slate-700">Notes:</span> {row.notes}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              {row.qualityCaseId != null ? (
                <Link className="text-sky-800 hover:underline" to={`/quality/cases/${row.qualityCaseId}`}>
                  Quality case {row.qualityCaseId}
                </Link>
              ) : null}
              {row.qualityIncidentId != null ? (
                <Link className="text-sky-800 hover:underline" to={qcIncidentPath(row.qualityIncidentId, "quality")}>
                  QC incident {row.qualityIncidentId}
                </Link>
              ) : null}
              <Link className="text-sky-800 hover:underline" to="/quality/analysis-loans">
                All open loans
              </Link>
            </div>
            {writesAllowed ? (
              <div className="max-w-md space-y-3 border-t border-slate-200 pt-4">
                <ZplLocationSelect
                  id="analysis-loan-detail-location"
                  value={station}
                  onChange={setStation}
                  unavailableMessage={unavailableMessage}
                  disabled={reprintMutation.isPending}
                
      documents={documents}
    />
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    disabled={!printerReady || reprintMutation.isPending}
                    onClick={() => reprintMutation.mutate()}
                  >
                    Print loan label
                  </Button>
                  {row.returnedAt == null ? (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void returnAnalysisTransfer(row.id).then(() => {
                          showToast({ tone: "success", message: "Marked returned." });
                          void detailQuery.refetch();
                        })
                      }
                    >
                      Mark returned
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
